import { useRouteError, isRouteErrorResponse, useNavigate } from 'react-router-dom';
import { getHttpStatus, isApiError } from '@/api/errors';
import { getErrorMessage } from '@/utils/errors/apiErrors';
import { Button } from '@/components/ui/button';
import { ErrorScreen } from '@/components/Errors/ErrorScreen';
import { SupportReportButton } from '@/components/Errors/SupportReportButton';

function copyForStatus(status: number | undefined, statusText?: string) {
  switch (status) {
    case 404:
      return { title: 'Page Not Found', message: "The page you're looking for doesn't exist." };
    case 401:
      return { title: 'Unauthorized', message: 'You need to be logged in to access this page.' };
    case 403:
      return { title: 'Access Denied', message: "You don't have permission to access this page." };
    case 500:
      return {
        title: 'Server Error',
        message: 'Something went wrong on our end. Please try again later.',
      };
    case undefined:
      return { title: 'Something went wrong', message: 'An unexpected error occurred.' };
    default:
      return { title: `Error ${status}`, message: statusText || 'An error occurred.' };
  }
}

const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError();
  const navigate = useNavigate();

  // Router-thrown responses carry their status directly; loader-thrown ApiErrors
  // carry it via getHttpStatus. Anything else gets the generic copy.
  const { title, message: fallbackMessage } = isRouteErrorResponse(error)
    ? copyForStatus(error.status, error.statusText)
    : copyForStatus(getHttpStatus(error));
  // ApiErrors get the app-wide curated copy (problem catalogue) over the generic line.
  const message = isApiError(error) ? getErrorMessage(error, fallbackMessage) : fallbackMessage;

  return (
    <ErrorScreen
      title={title}
      message={message}
      // Only real Errors get the diagnostics disclosure — a router-thrown response has no
      // message/stack to show.
      error={error instanceof Error ? error : undefined}
      size="wide"
    >
      <div className="flex justify-end gap-3 mt-4">
        <SupportReportButton error={error} className="mr-auto" />
        <Button onClick={() => window.location.reload()}>Retry</Button>
        <Button variant="outline" onClick={() => navigate('/')}>
          Home
        </Button>
      </div>
    </ErrorScreen>
  );
};

export default RouteErrorBoundary;
